/* eslint-disable no-undef */
import React, { useEffect, useRef, useState, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import {
  Container,
  ResizeHandle,
  MapControls,
  ControlGroup,
  ZoomControls,
  HoverBox,
  LocationFAB,
  // FullscreenButton,
} from './Map.styles'
import { MapProps, Feature, Source } from './Map.types'
import { MapControl } from '../../atoms/MapControl'
import { Icon } from '../../atoms/Icon'
// import { DrawingTools as ModularDrawingTools } from '../../molecules/DrawingTools'
import { MapDraw } from '../../molecules/MapDraw'
// import { PrintControl as ModularPrintControl } from '../../molecules/PrintControl'
import { MAPBOX_TOKEN, API_BASE_URL } from '../../../config'
import {
  transformTileRequest,
  addSourcesToMap,
  removeSources,
} from './Map.util'
import { useMapHandlers } from './Map.handlers'
import { useMapResize } from './hooks/core/useMapResize'
import { useMapBbox } from './hooks/core/useMapBbox'
import { useFlyToPosition } from './hooks/core/useFlyToPosition'
import { useUserLocation } from './hooks/core/useUserLocation'
// import { useFullscreen } from './hooks/core/useFullscreen'
import { useMeasurement } from './hooks/core/useMeasurement'
import { useMapStatePersistence } from './hooks/useMapStatePersistence'
import { useParcelLayers } from './hooks/layers/useParcelLayers'
import { useBuildingLayers } from './hooks/layers/useBuildingLayers'
import { useBaseLayerVisibility } from './hooks/layers/useBaseLayerVisibility'
import { useDemographicsLayers } from './hooks/layers/useDemographicsLayers'
import { useEmploymentLayers } from './hooks/layers/useEmploymentLayers'
import { usePricingLayers } from './hooks/layers/usePricingLayers'
import { useNoiseLayers } from './hooks/layers/useNoiseLayers'
import { useRestrictionsLayers } from './hooks/layers/useRestrictionsLayers'
import { useZoningVectorLayers } from './hooks/layers/useZoningVectorLayers'
import { useHistoricalImageryLayer } from './hooks/layers/useHistoricalImageryLayer'
import { useEnergyLayers } from './hooks/layers/useEnergyLayers'
import { useSunlightLayers } from './hooks/layers/useSunlightLayers'
import { useDynamicLayers } from './hooks/layers/useDynamicLayers'
import { MapControlsGroup } from './Map.controls'
import { useRouter } from '../../../hooks'

const threeDSource = 'mapbox-dem'

export const Map: React.FC<MapProps> = ({
  width,
  height = 600,
  center = [8.2275, 46.8182], // Switzerland center coordinates
  zoom = 8,
  data = [],
  markers = [],
  layers: _layers = [],
  legend: _legend = [],
  showControls = true,
  showDimensionControl = true,
  enableClustering: _enableClustering = false,
  enableMeasurement = true,
  mapStyle = 'mapbox://styles/popety/cm7mg342r00u001s21uzzd25t',
  mapOptions = {},
  HoverComponent,
  ClickComponent,
  Controller,
  onFeatureClick,
  onFeatureHover,
  onMarkerClick,
  onMapMove,
  onDragStart,
  onLayerToggle,
  onLegendClick: _onLegendClick,
  onFullScreenChange,
  dimension: dimensionProp = '2D',
  bbox,
  maxBbox = [5.3559, 45.3179, 11.0921, 48.3084],
  padding = 100,
  bboxTransitionDuration = 1000,
  autoFitBounds = false,
  isDragPan = true,
  flyToPosition,
  showFullScreenControl,
  fullScreen,
  defaultPitch = 0,
  showNavigation = true,
  className,
  style = {},
  children,
  LayersPanel: CustomLayersPanel,
  LegendPanel: CustomLegendPanel,
  PrintControl: CustomPrintControl,
  _showBuiltInDrawingTools = true,
  showBuiltInPrintControl = true,
  setLayersPanelOpen = () => {},
  layersPanelOpen = false,
  setLegendPanelOpen: externalSetLegendPanelOpen,
  legendPanelOpen: externalLegendPanelOpen,
  onChangeMapStyle,
  mapStylePanelOpen,
  onMapSearch,
  activeLayerImage,
  enableDrawing = false,
  initialDrawingData = [],
  onDrawingComplete,
  onDrawingUpdate,
  onDrawingDelete,
  drawingToolsProps,
  showFlyToPosition = true,
  geoJson,
  buildingGeoJson,
  buildingHeight,
  buildingClassification,
  dynamicLayersData,
  onDynamicLayerClick,
  persistenceContext,
  baseLayerStates,
  onBaseLayerStatesRestore,
  onMapStyleRestore,
  ...otherMapOptions
}) => {
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map | null>(null)
  const popup = useRef<mapboxgl.Popup | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [styleLoadedCount, setStyleLoadedCount] = useState(0)

  const [currentStyle, setCurrentStyle] = useState<string>(mapStyle)
  const [restorationComplete, setRestorationComplete] = useState(false)
  // const [drawToolsOpen, setDrawToolsOpen] = useState(false)
  const [internalLegendPanelOpen, setInternalLegendPanelOpen] = useState(false)
  const legendPanelOpen = externalLegendPanelOpen ?? internalLegendPanelOpen
  const setLegendPanelOpen =
    externalSetLegendPanelOpen ?? setInternalLegendPanelOpen
  // const [activeDrawTool, setActiveDrawTool] = useState<string | null>(null)
  const [dimension, setDimension] = useState(dimensionProp)
  const [hoverFeature, setHoverFeature] = useState<Feature>()
  const [clickFeature, setClickFeature] = useState<Feature>()
  const [isFullScreen, setIsFullScreen] = useState(fullScreen || false)

  // Track if a flyTo is in progress to prevent bbox from interrupting
  // Using ref instead of state to avoid re-renders that could trigger other effects
  const isFlyingToRef = useRef(false)

  // Track if initial bbox fit has been done to prevent repeated fits
  const hasInitiallyFit = useRef(false)

  const [mapBearing, setMapBearing] = useState(0)
  const { updateQuery } = useRouter()

  // Keep track of existing sources for cleanup
  const existingSources = useRef<Source[]>([])

  // Fullscreen functionality
  // const { isFullscreen, toggleFullscreen } = useFullscreen(mapContainer as any)

  // Measurement functionality
  const {
    isMeasuring,
    measurementPoints,
    previewPoint,
    distance,
    formattedDistance,
    startMeasurement,
    stopMeasurement,
    clearMeasurement,
    handleMapClick: handleMeasurementClick,
    handleMapMouseMove: handleMeasurementMouseMove,
  } = useMeasurement(map.current)

  // Map state persistence
  // Note: We don't persist center/zoom to allow each analysis to show its specific location
  const { savedState } = useMapStatePersistence({
    map: map.current,
    context: persistenceContext || '',
    currentState: {
      bearing: map.current?.getBearing(),
      pitch: map.current?.getPitch(),
      dimension,
      mapStyle: currentStyle,
      baseLayerStates: baseLayerStates,
      panelStates: {
        layers: layersPanelOpen,
        legend: legendPanelOpen,
        mapStyle: mapStylePanelOpen,
      },
    },
  })

  // Handle drawing tool changes
  const handleDrawingToolChange = useCallback(
    (tool: string) => {
      if (tool === 'measure') {
        startMeasurement()
      } else {
        if (isMeasuring) {
          stopMeasurement()
          clearMeasurement()
        }
      }
    },
    [startMeasurement, stopMeasurement, clearMeasurement, isMeasuring]
  )

  // Initialize Mapbox
  useEffect(() => {
    if (!mapContainer.current || map.current) return

    mapboxgl.accessToken = MAPBOX_TOKEN

    // Validate center coordinates
    const validatedCenter =
      center &&
      Array.isArray(center) &&
      center.length === 2 &&
      !isNaN(center[0]) &&
      !isNaN(center[1])
        ? center
        : [8.2275, 46.8182] // Switzerland center as fallback

    // Use saved state for initial view if available, otherwise use props
    // Note: We don't restore center/zoom from savedState to allow analysis-specific coordinates
    const initialCenter = validatedCenter
    const initialZoom = zoom
    const initialPitch = savedState?.pitch ?? defaultPitch
    const initialBearing = savedState?.bearing ?? 0
    const initialStyle = savedState?.mapStyle || mapStyle

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: initialStyle,
      center: initialCenter as [number, number],
      zoom: initialZoom,
      pitch: initialPitch,
      bearing: initialBearing,
      transformRequest: transformTileRequest,
      preserveDrawingBuffer: true, // Required for canvas export
      ...mapOptions,
      ...otherMapOptions,
    })

    popup.current = new mapboxgl.Popup({
      className: 'map-popup-no-padding',
      closeButton: false,
      closeOnClick: false,
    })

    // Add error handler for style evaluation errors
    map.current.on('error', (e) => {
      // Suppress non-critical style evaluation errors
      if (
        e.error &&
        e.error.message &&
        e.error.message.includes('Failed to evaluate expression')
      ) {
        console.debug('Mapbox style evaluation warning:', e.error.message)
        // Prevent the error from propagating if preventDefault exists
        if (typeof (e as any).preventDefault === 'function') {
          ;(e as any).preventDefault()
        }
      }
    })

    map.current.on('load', () => {
      setMapLoaded(true)
      setStyleLoadedCount((prev) => prev + 1)
      // Configure dragPan with optimized inertia settings for smoother panning
      // These settings provide better UX when interrupting inertia with a new drag
      if (map.current) {
        map.current.dragPan.enable({
          linearity: 0.15,
          maxSpeed: 1400,
          deceleration: 2500,
        })
      }

      // Add initial data sources
      if (data.length > 0) {
        addSourcesToMap(data, map.current!)
        existingSources.current = data
      }
      // Markers are handled by useEffect below
    })

    // Listen for style changes
    map.current.on('style.load', () => {
      setStyleLoadedCount((prev) => prev + 1)
    })

    map.current.on('moveend', () => {
      if (onMapMove && map.current) {
        const center = map.current.getCenter()
        const zoom = map.current.getZoom()
        onMapMove([center.lng, center.lat], zoom)
      }
    })

    // Track bearing changes for compass
    map.current.on('rotate', () => {
      if (map.current) {
        const bearing = map.current.getBearing()
        setMapBearing(bearing)
      }
    })

    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
      }
    }
  }, [])

  // Handle drag start event separately to ensure proper callback binding
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const handleDragStart = () => {
      if (onDragStart) {
        onDragStart()
      }
    }

    map.current.on('dragstart', handleDragStart)

    return () => {
      if (map.current) {
        map.current.off('dragstart', handleDragStart)
      }
    }
  }, [mapLoaded, onDragStart])

  // Restore saved state for dimension, style, panel states, and base layer states
  // This effect runs ONCE on mount to restore persisted state, then never again
  useEffect(() => {
    if (!savedState || !mapLoaded || restorationComplete) return

    let restored = false

    // Restore dimension mode
    if (savedState.dimension && savedState.dimension !== dimension) {
      setDimension(savedState.dimension)
      restored = true
    }

    // Restore map style if different
    if (savedState.mapStyle && savedState.mapStyle !== currentStyle) {
      setCurrentStyle(savedState.mapStyle)
      restored = true
      // Also notify parent component to update its map style state
      if (onMapStyleRestore) {
        onMapStyleRestore(savedState.mapStyle)
      }
    }

    // Restore panel states
    if (savedState.panelStates) {
      if (savedState.panelStates.layers !== undefined) {
        setLayersPanelOpen(savedState.panelStates.layers)
        restored = true
      }
      if (savedState.panelStates.legend !== undefined) {
        setLegendPanelOpen(savedState.panelStates.legend)
        restored = true
      }
    }

    // Restore base layer states (for base layers like streets, labels, terrain, etc.)
    if (savedState.baseLayerStates && onBaseLayerStatesRestore) {
      onBaseLayerStatesRestore(savedState.baseLayerStates)
      restored = true
    }

    // Mark restoration as complete after first run
    // This prevents the effect from re-running when currentStyle or dimension changes
    if (restored) {
      setRestorationComplete(true)
    }
  }, [
    savedState,
    mapLoaded,
    restorationComplete,
    // dimension,  // REMOVED: Prevents re-triggering on user changes
    // currentStyle,  // REMOVED: This was causing the race condition!
    onBaseLayerStatesRestore,
    onMapStyleRestore,
  ])

  // Handle measurement clicks and mouse move
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const handleClick = (e: mapboxgl.MapMouseEvent) => {
      if (isMeasuring) {
        handleMeasurementClick(e)
      }
    }

    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      if (isMeasuring) {
        handleMeasurementMouseMove(e)
      }
    }

    map.current.on('click', handleClick)
    map.current.on('mousemove', handleMouseMove)

    return () => {
      if (map.current) {
        map.current.off('click', handleClick)
        map.current.off('mousemove', handleMouseMove)
      }
    }
  }, [
    mapLoaded,
    isMeasuring,
    handleMeasurementClick,
    handleMeasurementMouseMove,
  ])

  // Initialize measurement layers once
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const measurementSourceId = 'measurement-source'
    const measurementLineLayerId = 'measurement-line'
    const measurementCircleLayerId = 'measurement-circle'
    const measurementPointsLayerId = 'measurement-points'
    const measurementLabelLayerId = 'measurement-label'

    // Only create layers if they don't exist
    if (!map.current.getSource(measurementSourceId)) {
      // Add empty source
      map.current.addSource(measurementSourceId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      })

      // Add circle layer (radius visualization)
      map.current.addLayer({
        id: measurementCircleLayerId,
        type: 'line',
        source: measurementSourceId,
        filter: ['==', ['get', 'isCircle'], true],
        paint: {
          'line-color': '#00ade6',
          'line-width': 2,
          'line-opacity': 0.3,
          'line-dasharray': [4, 4],
        },
      })

      // Add line layer
      map.current.addLayer({
        id: measurementLineLayerId,
        type: 'line',
        source: measurementSourceId,
        filter: ['==', '$type', 'LineString'],
        paint: {
          'line-color': '#00ade6',
          'line-width': 3,
          'line-dasharray': [2, 2],
        },
      })

      // Add label layer
      map.current.addLayer({
        id: measurementLabelLayerId,
        type: 'symbol',
        source: measurementSourceId,
        filter: ['all', ['==', '$type', 'Point'], ['has', 'distance']],
        layout: {
          'text-field': ['get', 'distance'],
          'text-size': 16,
          'text-offset': [0, -2],
          'text-anchor': 'bottom',
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
        },
        paint: {
          'text-color': '#00ade6',
          'text-halo-color': '#ffffff',
          'text-halo-width': 3,
          'text-halo-blur': 1,
        },
      })

      // Add points layer
      map.current.addLayer({
        id: measurementPointsLayerId,
        type: 'circle',
        source: measurementSourceId,
        filter: ['all', ['==', '$type', 'Point'], ['!', ['has', 'distance']]],
        paint: {
          'circle-radius': ['case', ['==', ['get', 'isPreview'], true], 5, 7],
          'circle-color': '#00ade6',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2,
          'circle-opacity': [
            'case',
            ['==', ['get', 'isPreview'], true],
            0.6,
            1,
          ],
        },
      })
    }

    return () => {
      if (map.current) {
        if (map.current.getLayer(measurementLabelLayerId)) {
          map.current.removeLayer(measurementLabelLayerId)
        }
        if (map.current.getLayer(measurementCircleLayerId)) {
          map.current.removeLayer(measurementCircleLayerId)
        }
        if (map.current.getLayer(measurementLineLayerId)) {
          map.current.removeLayer(measurementLineLayerId)
        }
        if (map.current.getLayer(measurementPointsLayerId)) {
          map.current.removeLayer(measurementPointsLayerId)
        }
        if (map.current.getSource(measurementSourceId)) {
          map.current.removeSource(measurementSourceId)
        }
      }
    }
  }, [mapLoaded])

  // Update measurement data in real-time
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const measurementSourceId = 'measurement-source'
    const source = map.current.getSource(
      measurementSourceId
    ) as mapboxgl.GeoJSONSource
    if (!source) return

    // If no measurement points, clear the source
    if (measurementPoints.length === 0 && !previewPoint) {
      source.setData({
        type: 'FeatureCollection',
        features: [],
      })
      return
    }

    // Create GeoJSON for measurement
    const geojson: any = {
      type: 'FeatureCollection',
      features: [],
    }

    // Add confirmed points
    measurementPoints.forEach((point, index) => {
      geojson.features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [point.lng, point.lat],
        },
        properties: {
          index,
          isPreview: false,
        },
      })
    })

    // Add preview point if we have one
    if (previewPoint) {
      geojson.features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [previewPoint.lng, previewPoint.lat],
        },
        properties: {
          index: -1,
          isPreview: true,
        },
      })
    }

    // Determine which points to connect with a line
    const linePoints =
      measurementPoints.length === 1 && previewPoint
        ? [measurementPoints[0], previewPoint]
        : measurementPoints.length === 2
          ? measurementPoints
          : null

    // Add line if we have 2 points (confirmed or preview)
    if (linePoints) {
      geojson.features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: linePoints.map((p) => [p.lng, p.lat]),
        },
        properties: {
          isPreview: previewPoint !== null,
        },
      })

      // Add circle for radius visualization
      if (distance && measurementPoints.length > 0) {
        // Create a circle polygon using the first point as center and distance as radius
        const center = measurementPoints[0]
        const radiusInKm = distance / 1000 // Convert meters to km
        const points = 64
        const coordinates: number[][] = []

        for (let i = 0; i <= points; i++) {
          const angle = (i * 360) / points
          const latitude =
            center.lat +
            (radiusInKm / 111.32) * Math.cos((angle * Math.PI) / 180)
          const longitude =
            center.lng +
            ((radiusInKm / 111.32) * Math.sin((angle * Math.PI) / 180)) /
              Math.cos((center.lat * Math.PI) / 180)
          coordinates.push([longitude, latitude])
        }

        geojson.features.push({
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates],
          },
          properties: {
            isCircle: true,
          },
        })
      }

      // Add label at midpoint
      const midLng = (linePoints[0].lng + linePoints[1].lng) / 2
      const midLat = (linePoints[0].lat + linePoints[1].lat) / 2
      geojson.features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [midLng, midLat],
        },
        properties: {
          distance: formattedDistance,
        },
      })
    }

    // Update source data directly (real-time update)
    source.setData(geojson)
  }, [mapLoaded, measurementPoints, previewPoint, formattedDistance, distance])

  // Observe container size changes and resize map
  useEffect(() => {
    if (!mapContainer.current || !map.current) return

    const resizeObserver = new ResizeObserver(() => {
      // Debounce the resize to avoid too many calls
      if (map.current) {
        // Small timeout to ensure the map container has finished resizing
        setTimeout(() => {
          if (map.current) {
            map.current.resize()
          }
        }, 150)
      }
    })

    resizeObserver.observe(mapContainer.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [mapLoaded])

  // Update data sources
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    // Remove existing sources
    removeSources(existingSources.current, map.current)

    // Add new sources
    addSourcesToMap(data, map.current)
    existingSources.current = data

    // Re-enable event handlers for new layers
    enableMapEvents()

    return () => {
      disableMapEvents()
    }
  }, [data, mapLoaded])

  // Handle fullscreen change
  useEffect(() => {
    setIsFullScreen(fullScreen || false)
  }, [fullScreen])

  // Map event handlers
  const {
    enableMapEvents,
    disableMapEvents,
    addMarkers: handleAddMarkers,
    removeMarkers: handleRemoveMarkers,
  } = useMapHandlers({
    map,
    popup,
    mapLoaded,
    data,
    onFeatureClick,
    onFeatureHover,
    onMarkerClick,
    setHoverFeature,
    setClickFeature,
  })

  // Handle map resize
  useMapResize({ map, mapLoaded, width, height })

  // Handle bbox fitting
  useMapBbox({
    map,
    mapLoaded,
    bbox,
    maxBbox,
    padding,
    autoFitBounds,
    isDragPan,
    bboxTransitionDuration,
    isFlyingToRef,
    hasInitiallyFit,
  } as any)

  // Handle fly to position
  useFlyToPosition({ map, mapLoaded, flyToPosition, isFlyingToRef } as any)

  // Handle user location (or property location if center is provided for analysis maps)
  const { handleFlyToUserLocation } = useUserLocation({
    map,
    mapLoaded,
    propertyCoordinates: center as [number, number] | undefined,
  })

  // Handle parcel layers
  useParcelLayers({
    map,
    mapLoaded,
    geoJson,
    layers: _layers,
    styleLoadedCount,
  })

  // Handle building layers
  useBuildingLayers({
    map,
    mapLoaded,
    buildingGeoJson,
    buildingHeight,
    buildingClassification,
    layers: _layers,
    styleLoadedCount,
  })

  // Handle base layer visibility
  useBaseLayerVisibility({
    map,
    mapLoaded,
    layers: _layers,
    currentStyle,
    mapStyle,
    styleLoadedCount,
  })

  // Handle demographics layers
  useDemographicsLayers({
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle employment layers
  useEmploymentLayers({
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle pricing layers
  usePricingLayers({
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle noise layers
  useNoiseLayers({
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle restrictions layers
  useRestrictionsLayers({
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle energy layers (solar potential roof polygons)
  useEnergyLayers({
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle sunlight layers (shadow simulation and sunlight hours)
  useSunlightLayers({
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle zoning GeoJSON layers (using batch API data)
  useZoningVectorLayers(map, mapLoaded, styleLoadedCount, otherMapOptions)

  // Handle historical imagery layer (raster tiles)
  useHistoricalImageryLayer({
    map,
    mapLoaded,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle dynamic layers
  useDynamicLayers({
    map,
    mapLoaded,
    popup,
    dynamicLayersData,
    styleLoadedCount,
    onDynamicLayerClick,
  } as any)

  // Add markers effect
  useEffect(() => {
    if (mapLoaded) {
      handleAddMarkers(markers)
    }
    // Cleanup markers on unmount
    return () => {
      handleRemoveMarkers()
    }
  }, [markers, mapLoaded, handleAddMarkers, handleRemoveMarkers])

  // Sync currentStyle with mapStyle prop changes
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    if (mapStyle !== currentStyle) {
      setCurrentStyle(mapStyle)

      // Actually update the map style
      map.current.setStyle(mapStyle)

      // Listen for when the new style finishes loading
      const handleStyleLoad = () => {
        console.log('New style loaded, incrementing styleLoadedCount')
        setStyleLoadedCount((prev) => prev + 1)
      }

      // Add one-time listener for style.load event
      map.current.once('style.load', handleStyleLoad)
    }
  }, [mapStyle, mapLoaded])

  // Handle zoom controls
  const handleZoomIn = useCallback(() => {
    if (map.current) map.current.zoomIn()
  }, [])

  // Placeholder functions for event handlers (will be replaced by actual handlers in the useEffect)
  // const handleDynamicLayerClick = useCallback(() => {}, [])
  // const handleClusterClick = useCallback(() => {}, [])

  const handleZoomOut = useCallback(() => {
    if (map.current) map.current.zoomOut()
  }, [])

  // Handle compass click to reset to north
  const handleCompassClick = useCallback(() => {
    if (map.current) {
      map.current.easeTo({
        bearing: 0,
        duration: 1000,
      })
    }
  }, [])

  // Handle 3D view toggle
  const handleToggle3D = useCallback(() => {
    if (!map.current) return

    const newDimension = dimension === '2D' ? '3D' : '2D'
    setDimension(newDimension)
    updateQuery({ dimension: newDimension })

    if (newDimension === '3D') {
      map.current.addSource(threeDSource, {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
      })
      // add the DEM source as a terrain layer with exaggerated height
      map.current.setTerrain({ source: threeDSource, exaggeration: 1 })
      map.current.setPitch(60)
      map.current.setBearing(-17.6)
    } else {
      map.current.setTerrain()
      map.current.removeSource(threeDSource)
      // we set the pitch back to 0 as we are in 2D
      map.current.setPitch(defaultPitch)
      map.current.setBearing(0)
    }
  }, [dimension, defaultPitch])

  // Handle draw tool selection
  // const handleDrawTool = useCallback(
  //   (toolId: string) => {
  //     setActiveDrawTool(activeDrawTool === toolId ? null : toolId)
  //     setDrawToolsOpen(false)

  //     // Drawing functionality would be implemented here
  //     // You could add mapbox-gl-draw or custom drawing logic
  //   },
  //   [activeDrawTool]
  // )

  // // Handle delete drawings
  // const handleDeleteDrawings = useCallback(() => {
  //   setActiveDrawTool(null)
  //   setDrawToolsOpen(false)

  //   // Deletion functionality would be implemented here
  // }, [])

  // // Handle thematic layer change
  // const handleThematicLayerChange = useCallback(
  //   (layerId: string, enabled: boolean) => {
  //     setSelectedThematicLayer(enabled ? layerId : '')
  //     setLegendPanelOpen(enabled && showLegend)

  //     // Thematic layer visibility would be implemented here
  //   },
  //   [showLegend]
  // )

  // Handle fullscreen toggle
  const handleToggleFullScreen = useCallback(() => {
    setIsFullScreen((prev) => !prev)
    onFullScreenChange?.()
  }, [])

  // Prepare common props for child components
  const commonProps = {
    map: map.current ?? undefined,
    popup: popup.current ?? undefined,
    mapboxgl,
    clickFeature,
    setClickFeature,
    loaded: mapLoaded,
    dimension,
    setDimension,
  }

  // Clone children with props
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, commonProps as any)
    }
    return child
  })

  const minHeight = isFullScreen ? 'calc(100vh - 160px)' : '400px'

  return (
    <Container
      className={className}
      style={{
        ...(style as any),
        ...(isFullScreen && {
          minHeight,
          overflow: 'hidden',
        }),
      }}
      $width={width}
      $height={height}
    >
      {/* Resize Handle */}
      <ResizeHandle />

      {/* Map Controls */}
      {showControls && (
        <MapControls>
          <ControlGroup>
            <MapControlsGroup
              showControls={showControls}
              layersPanelOpen={layersPanelOpen}
              setLayersPanelOpen={setLayersPanelOpen}
              legendPanelOpen={legendPanelOpen}
              setLegendPanelOpen={setLegendPanelOpen}
              showDimensionControl={showDimensionControl}
              dimension={dimension}
              onToggle3D={handleToggle3D}
              showBuiltInPrintControl={showBuiltInPrintControl}
              map={map}
              mapContainer={mapContainer}
              CustomPrintControl={CustomPrintControl}
              onChangeMapStyle={onChangeMapStyle}
              mapStylePanelOpen={mapStylePanelOpen}
              activeLayerImage={activeLayerImage}
              onFlyToUserLocation={handleFlyToUserLocation}
            />
          </ControlGroup>
        </MapControls>
      )}

      {/* Location FAB */}
      {showFlyToPosition && (
        <LocationFAB
          onClick={handleFlyToUserLocation}
          data-tooltip={center ? 'Fly to property' : 'Fly to my location'}
          aria-label={center ? 'Fly to property' : 'Fly to my location'}
        >
          <Icon name="crosshair" size="medium" />
        </LocationFAB>
      )}
      {/* Zoom Controls */}
      {showNavigation && (
        <ZoomControls>
          <ControlGroup>
            {/* Compass Control - only show when map is rotated */}

            <MapControl
              style={{
                borderRadius: '10px 10px 0 0',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              iconStyle={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              icon={
                <Icon
                  name="compass"
                  style={{
                    transform: `rotate(${-mapBearing}deg)`,
                  }}
                />
              }
              title="Reset to North"
              onClick={handleCompassClick}
            />

            <MapControl
              style={{
                borderRadius: '0',
              }}
              icon={<Icon size={'small'} name="plus" />}
              title="Zoom In"
              onClick={handleZoomIn}
            />
            <MapControl
              style={{ borderRadius: '0 0 10px 10px' }}
              icon={<Icon size={'small'} name="minus" />}
              title="Zoom Out"
              onClick={handleZoomOut}
            />
          </ControlGroup>
        </ZoomControls>
      )}

      {/* Fullscreen Control */}
      {showFullScreenControl && (
        <MapControl
          icon={
            <svg viewBox="0 0 24 24">
              <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
            </svg>
          }
          title={isFullScreen ? 'Exit Fullscreen' : 'Fullscreen'}
          onClick={handleToggleFullScreen}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 10,
          }}
        />
      )}

      {/* Drawing Tools */}
      {enableMeasurement && enableDrawing && (
        <MapDraw
          map={map.current}
          enabled={enableDrawing}
          onDrawingComplete={onDrawingComplete}
          onDrawingUpdate={onDrawingUpdate}
          onDrawingDelete={onDrawingDelete}
          onToolChange={handleDrawingToolChange}
          initialData={initialDrawingData}
          drawingToolsProps={drawingToolsProps}
        />
      )}

      {/* Hover Component */}
      {hoverFeature && HoverComponent && (
        <HoverBox
          className="HoverBox"
          left={hoverFeature.x}
          top={hoverFeature.y}
        >
          <HoverComponent {...hoverFeature} />
        </HoverBox>
      )}

      {/* Click Component */}
      {ClickComponent && (
        <ClickComponent
          {...commonProps}
          {...clickFeature}
          setClickFeature={setClickFeature}
        />
      )}

      {/* Controller Component */}
      {Controller && <Controller {...commonProps} />}

      {/* Map Container */}
      <div
        ref={mapContainer}
        className="map-container"
        style={{ width: '100%', height: '100%' }}
      />

      {/* Children with map context */}
      {childrenWithProps}
    </Container>
  )
}
